import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Form, Validators } from '@angular/forms';
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { ConfiguracionContextService } from '../../context/configuracion-context.service';
import { ConfiguracionService } from '../../services/configuracion.service';

@Component({
    selector: 'app-configurador-consumos',
    templateUrl: './configurador-consumos-integracion.component.html'
})
export class ConfiguradorConsumosComponent implements OnInit, OnDestroy {

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;
    private _filters;
    public filtersVisible = false;
    public filtersForm: UntypedFormGroup;

    public toCloseSpinner = 0;

    public orderBy: string;
    public columnId: number;

    public almacenes;
    public marcas;
    public configuraciones;
    public configuracionId;

    public disabledEtiquetadores: Subject<boolean> = new Subject();
    public disabledAlmacenes: Subject<boolean> = new Subject();
    public disabledMarcas: Subject<boolean> = new Subject();

    public mensajes = new Array<Message>();
    public showError: boolean;
    public showWarning: boolean;

    public deleteModal = false;
    public newEditModal = false;

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 0;
    public totalItems: number;
    public readonly visiblePages = 3;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _sessionContext: SessionContextService,
        private readonly _configuracionService: ConfiguracionService,
        private readonly _configuracionContext: ConfiguracionContextService,
        private readonly _spinnerService: SpinnerService,
        private readonly _dataProvider: DataProvider,
        private readonly _utilsService: UtilsService,
        private readonly _translate: TranslateService
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            etiquetador: ['', Validators.required],
            almacen: [{value: '', disabled: true}],
            marca: [{value: '', disabled: true}],
            designacion: [''],
            codigoIntegracion: [''],
            paisDestino: [''],
            consumosImpresos: [''],
            fechaInicio: [''],
            fechaFin: [''],
            dateType: ['false']
        });

        this._configuracionContext.configuracionConsumosFilters = null;
        this.checkWarnings();

        this.filtersForm.get('etiquetador').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((etiquetador) => {
            if (etiquetador) {
                this.getAlmacenesByEtiquetador(etiquetador.id);
                this.getMarcasByEtiquetador(etiquetador.id);
                this.filtersForm.get('almacen').setValidators(Validators.required);
                this.filtersForm.get('almacen').updateValueAndValidity({onlySelf: true, emitEvent: false});
                this.disabledAlmacenes.next(false);
                this.disabledMarcas.next(false);
            } else {
                this.disabledAlmacenes.next(true);
                this.disabledMarcas.next(true);
                this.almacenes = [];
                this.marcas = [];
                this.filtersForm.get('almacen').patchValue('');
                this.filtersForm.get('almacen').disable();
                this.filtersForm.get('marca').disable();
                this.filtersForm.get('marca').patchValue('');
            }
        });

        this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
        this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get role() {
        return this._sessionContext.sessionUser.role;
    }

    public get claims() {
        return this._sessionContext.sessionUser.lsClaimTypes;
    }

    public get etiquetadores() {
        if (this._dataProvider.etiquetadores && this._dataProvider.etiquetadores.length === 1) {
            this.filtersForm.get('etiquetador').patchValue(this._dataProvider.etiquetadores[0]);
            this.filtersForm.get('etiquetador').disable();
            this.disabledEtiquetadores.next(true);
        }

        return this._dataProvider.etiquetadores;
    }

    public get destinos() {
        return this._dataProvider.destinos;
    }

    public get defaultLanguage() {
      return this._translate.getDefaultLang();
    }

    public get isSmallScreen(){
      return this._sessionContext.isSmallScreen();
    }

    public getAlmacenesByEtiquetador(etiquetadorId) {
        let filters = {
            etiquetadorId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._configuracionService.getAlmacenesByEtiquetadorId(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.almacenes = resp.lista;
                if (this.almacenes.length === 1) {
                    this.filtersForm.get('almacen').patchValue(this.almacenes[0]);
                    this.filtersForm.get('almacen').disable();
                    this.disabledAlmacenes.next(true);
                } else {
                    this.disabledAlmacenes.next(false);
                    this.filtersForm.get('almacen').enable();
                }
            }
        });
    }

    public getMarcasByEtiquetador(etiquetadorId) {
        let filters = {
            etiquetadorId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._configuracionService.getMarcasByEtiquetadorId(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.marcas = resp.lista;
                this.filtersForm.get('marca').enable();
                this.disabledMarcas.next(false);
            } else {
                this.filtersForm.get('marca').disable();
                this.disabledMarcas.next(true);
            }
        });
    }

    private checkWarnings() {
        if (this.filtersForm.valid) {
            this.filtersVisible = false;
            this.showWarning = false;
            this.mensajes = [];
            this.getConfiguraciones();
        } else {
            this.filtersVisible = true;
            this.showWarning = true;
            this.mensajes.push({ class: 'warning', message: 'global.info.filterRequired' });
        }
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
            this._spinnerService.hideSpinner();
        }
    }

    public pageChange(page) {
        this._configuracionContext.configuracionConsumosFilters.numPagina = this.currentPage = page - 1;
        this.getConfiguraciones();
    }

    public itemsPerPageChange(items) {
        this._configuracionContext.configuracionConsumosFilters.numPagina = this.currentPage = 0;
        this._configuracionContext.configuracionConsumosFilters.tamPagina = this.itemsPerPage = items;
        this.getConfiguraciones();
    }

    public validDates() {
        if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
            if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
                this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio' });
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private clearDatesValidators() {
        if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaFin').clearValidators();
            this.filtersForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaInicio').clearValidators();
            this.filtersForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    public checkValuesForm(): boolean {
        return (this.filtersForm.get('etiquetador').value  && this.filtersForm.get('etiquetador').enabled ||
        this.filtersForm.get('almacen').value ||
        this.filtersForm.get('marca').value ||
        this.filtersForm.get('codigoIntegracion').value ||
        this.filtersForm.get('consumosImpresos').value ||
        this.filtersForm.get('fechaInicio').value ||
        this.filtersForm.get('fechaFin').value) ? true : false;
    }

    public resetForm() {
        this.filtersForm.reset({
            etiquetador: this.filtersForm.get('etiquetador').disabled ? this.filtersForm.get('etiquetador').value : null,
            dateType: 'false'
        });
    }

    public filtrar() {
        this.validDates();
        if (this.filtersForm.invalid) {
            if (Object.keys(this.filtersForm.controls).some(key => this.filtersForm.get(key).hasError('required'))) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
        } else if (this.filtersForm.valid) {
            this.applyFilters();
        }
    }

    private applyFilters() {
        this.currentPage = 0;
        this._configuracionContext.configuracionConsumosFilters = {
            etiquetadorId: this.filtersForm.get('etiquetador').value ? this.filtersForm.get('etiquetador').value.id : null,
            almacenId: this.filtersForm.get('almacen').value ? this.filtersForm.get('almacen').value.id : null,
            marcaId: this.filtersForm.get('marca').value ? this.filtersForm.get('marca').value.id : null,
            destinoId: this.filtersForm.get('paisDestino').value ? this.filtersForm.get('paisDestino').value.id : null,
            codigoIntegracion: this.filtersForm.get('codigoIntegracion').value ? this.filtersForm.get('codigoIntegracion').value : '',
            consumosImpresos: this.filtersForm.get('consumosImpresos').value ? this.filtersForm.get('consumosImpresos').value : '',
            fechaInicio: this.filtersForm.get('fechaInicio').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
            fechaFin: this.filtersForm.get('fechaFin').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
            fechaActualizacion: this.filtersForm.get('dateType').value,
            numPagina: this.currentPage,
            tamPagina: this.itemsPerPage,
        };

        this.getConfiguraciones();
    }

    public getConfiguraciones() {
        if (this._configuracionContext.configuracionConsumosFilters) {
            this._filters = this._configuracionContext.configuracionConsumosFilters;
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this._configuracionService.getConfiguracionesConsumos(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.totalItems = resp.paginacionMeta.totalCount;
            this.configuraciones = resp.lista;
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });

    }

    public create() {
        this.configuracionId = null;
        this.newEditModal = true;
    }

    public update(configuracion) {
        this.configuracionId = configuracion.id;
        this.newEditModal = true;
    }

    public delete() {
        this._spinnerService.showSpinner();
        this._configuracionService.deleteConfiguradorConsumo(this.configuracionId).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then(() => {
            this.close();
            this.pushMessage('global.success.itemDeleted');
            this.checkWarnings();
        });
    }

    public askDelete(configuracion) {
        this.configuracionId = configuracion.id;
        this.deleteModal = true;
    }

    public close() {
        this.deleteModal = false;
        this.newEditModal = false;
    }

    public refresh(value) {
        this.newEditModal = false;
        if (value) {
            this.checkWarnings();
        }
    }

    public pushMessage(mensaje) {
        this.showError = true;
        this.mensajes.push({ class: 'success', message: mensaje });
    }

    public getIcon(prop: string, idColumn: number) {
        let iconClass = 'fa fa-sort';
        if (this._filters && this._filters.orderItems && this._filters.orderItems[0].orderProperty.indexOf(prop) != -1 && this.columnId === idColumn) {
            iconClass = this._filters.orderItems[0].orderByDescending === true ? 'fa fa-sort-down' : 'fa fa-sort-up';
        }
        return iconClass;
    }

    public toOrderBy(columnFilter, idColumn) {
        this.columnId = idColumn;
        if (this.configuraciones && this.configuraciones.length > 0) {
            if (this._configuracionContext.configuracionConsumosFilters) {
                this._filters = this._configuracionContext.configuracionConsumosFilters;
                if (!this.orderBy || this.orderBy.includes('DESC')) {
                    this.orderBy = 'ASC' + idColumn;
                    this._filters.orderItems = [
                        {
                            orderProperty: columnFilter,
                            orderByDescending: false
                        }
                    ];
                } else if (this.orderBy.includes('ASC')) {
                    this.orderBy = 'DESC' + idColumn;
                    this._filters.orderItems = [
                        {
                            orderProperty: columnFilter,
                            orderByDescending: true
                        }
                    ];
                }

                this._spinnerService.showSpinner();
                this._configuracionService.getConfiguracionesConsumos(this._filters).toPromise().finally(() => {
                    this._spinnerService.hideSpinner();
                }).then((resp) => {
                    this.configuraciones = resp.lista;
                });
            }
        }
    }

    public trackByConfiguracionId(index: number, configuracion: { codigoIntegracion: number }): number {
        return configuracion.codigoIntegracion;
    }
}
